import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { VSpacingContainer, CustomChip, ChipsContainer } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { useGeoFiltersMetroRedesignExp } from 'src/components/Search/Resume/Filters/Geo/hooks/useGeoFiltersRedesignExp';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { CurrentGeoFilter, setSearchGeoDrawerCurrentFilter } from 'src/models/search/common/geoDrawer';
import { ClusterKey } from 'src/types/search/common/clusters';

import District from 'src/components/Search/Resume/Filters/Geo/Filters/District';
import Metro from 'src/components/Search/Resume/Filters/Geo/Filters/Metro';

const TrlKeys = {
    metroTitle: 'search.resume.filters.geo.metro.title',
    districtTitle: 'search.resume.filters.geo.district.title',
};

const Filters: TranslatedComponent = ({ trls }) => {
    const dispatch = useDispatch();
    const isGeoFiltersMetroRedesignExp = useGeoFiltersMetroRedesignExp();

    const currentGeoFilter = useSelector((state) => state.searchGeoDrawer.currentGeoFilter);
    const metroCluster = useSelector((state) => state.searchDraftClusters[ClusterKey.Metro]);
    const districtCluster = useSelector((state) => state.searchDraftClusters[ClusterKey.District]);

    const areaCluster = useSelector((state) => state.searchDraftClusters[ClusterKey.Area]);
    const selectedAreaId = areaCluster?.selectedValues?.[0] || '';
    const metroCities = useSelector((state) => state.metroCities);
    const isMetroAvailable = useMemo(
        () => isGeoFiltersMetroRedesignExp && metroCities.includes(String(selectedAreaId)),
        [isGeoFiltersMetroRedesignExp, metroCities, selectedAreaId]
    );
    const metroCache = useSelector((state) => state.searchGeoDrawer.metroCache[selectedAreaId]);

    const handleMetroChipsClick = useCallback(() => {
        dispatch([setSearchGeoDrawerCurrentFilter(CurrentGeoFilter.METRO)]);
    }, [dispatch]);

    const handleDistrictChipsClick = useCallback(() => {
        dispatch([setSearchGeoDrawerCurrentFilter(CurrentGeoFilter.DISTRICT)]);
    }, [dispatch]);

    const isMetroSelected = currentGeoFilter === CurrentGeoFilter.METRO;
    const isDistrictSelected = currentGeoFilter === CurrentGeoFilter.DISTRICT;

    const selectedMetroCounter = useMemo(() => {
        let counter = 0;

        metroCluster?.selectedValues?.forEach((selectedValueId) => {
            const splitedId = selectedValueId.split('.');
            const isLine = splitedId.length === 1;

            // Не может быть одновременно выбрана и линия, и её дочерние станции.
            if (isLine) {
                const lineId = splitedId[0];
                counter += metroCache?.find((line) => line.id === lineId)?.items?.length || 0;
            } else {
                counter += 1;
            }
        });

        return counter ? String(counter) : undefined;
    }, [metroCache, metroCluster]);

    const selectedDistrictsCounter = districtCluster?.selectedValues?.length
        ? String(districtCluster.selectedValues.length)
        : undefined;

    return (
        <VSpacingContainer default={16}>
            <ChipsContainer>
                {isMetroAvailable && (
                    <CustomChip
                        size="small"
                        Element="button"
                        postfix={selectedMetroCounter}
                        checked={isMetroSelected}
                        onClick={handleMetroChipsClick}
                    >
                        {trls[TrlKeys.metroTitle]}
                    </CustomChip>
                )}
                <CustomChip
                    size="small"
                    Element="button"
                    postfix={selectedDistrictsCounter}
                    checked={isDistrictSelected}
                    onClick={handleDistrictChipsClick}
                >
                    {trls[TrlKeys.districtTitle]}
                </CustomChip>
            </ChipsContainer>
            {isMetroAvailable && isMetroSelected && <Metro />}
            {isDistrictSelected && <District />}
        </VSpacingContainer>
    );
};

export default translation(Filters);
